import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@edusense/shared-auth-core';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import PaljaBookCatalogPanel from '../books/PaljaBookCatalogPanel';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

// 사주 책방 관리 — sajug 백엔드(/api/admin/books·orders·email-logs)와 연동
// 백엔드 스펙: services/sajug-api docs "50.사주앱 주문 방법.md" §24

interface BookStats {
  products: number;
  ordersTotal: number;
  ordersPaid: number;
  ordersUnsent: number;
  ordersSent: number;
  ordersShipped: number;
  revenue: number;
  emailFailed: number;
  reviews: number;
  reviewsHidden: number;
  mileageIssued: number;
}

interface BookOrder {
  orderId: string;
  status: string;
  amount: number;
  amountDue?: number;
  mileageUsed?: number;
  email: string;
  ordererName: string;
  recipientName?: string;
  targetName: string;
  birthDate: string;
  birthTime?: string | null;
  calendarType: 'SOLAR' | 'LUNAR';
  gender?: string;
  dst?: boolean;
  deliveryStatus?: string | null;
  deliveryDueAt?: string | null;
  sentAt?: string | null;
  pdfAvailable?: boolean;
  pdfFileName?: string | null;
  downloadCount?: number;
  downloadExpiresAt?: string | null;
  createdAt?: string;
  items: Array<{ title: string; quantity: number; kind: string; unitPrice?: number; listPrice?: number; lineTotal?: number }>;
}

interface BookProduct {
  productId: string;
  title: string;
  kind: 'pdf' | 'physical';
  listPrice: number;
  price: number;
  emoji: string;
  description: string;
  author?: string;
  coverUrl?: string;
  detailHtml: string;
  active: boolean;
  sortOrder: number;
  stock: number | null;
  salesCount: number;
  reviewCount: number;
  avgRating: number;
}

interface BookReview {
  id: number;
  product_id: string;
  author_name: string;
  email: string;
  rating: number;
  content: string;
  status: string;
  created_at: string;
}

interface EmailLog {
  id: number;
  order_id: string;
  to_email: string;
  status: string;
  error?: string | null;
  attempt: number;
  sent_at?: string | null;
}

type TabKey = 'palja' | 'orders' | 'unsent' | 'sent' | 'products' | 'reviews' | 'emails';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'palja', label: '팔자폭격 책방' },
  { key: 'orders', label: '주문' },
  { key: 'unsent', label: '비발송' },
  { key: 'sent', label: '발송' },
  { key: 'products', label: '기존 사주 상품/스토어' },
  { key: 'reviews', label: '리뷰' },
  { key: 'emails', label: '발송 로그' },
];

const EMPTY_PRODUCT: BookProduct = {
  productId: '',
  title: '',
  kind: 'pdf',
  listPrice: 0,
  price: 0,
  emoji: '📕',
  description: '',
  author: '',
  coverUrl: '',
  detailHtml: '',
  active: true,
  sortOrder: 0,
  stock: null,
  salesCount: 0,
  reviewCount: 0,
  avgRating: 0,
};

const krw = (v: number) => `${(v ?? 0).toLocaleString('ko-KR')}원`;

// 주문 아이템에서 정가합/할인율 계산 (정가 정보가 없으면 null)
const orderDiscount = (order: BookOrder) => {
  let listTotal = 0;
  let sellTotal = 0;
  for (const i of order.items) {
    const qty = i.quantity ?? 1;
    const unit = i.unitPrice ?? 0;
    const list = i.listPrice ?? unit;
    listTotal += list * qty;
    sellTotal += i.lineTotal ?? unit * qty;
  }
  if (listTotal <= 0 || listTotal <= sellTotal) return null;
  const amount = listTotal - sellTotal;
  return { listTotal, amount, rate: Math.round((amount / listTotal) * 100) };
};

export default function BookAdminPage() {
  const { token: accessToken } = useAuth();
  const [tab, setTab] = useState<TabKey>('palja');
  const [stats, setStats] = useState<BookStats | null>(null);
  const [orders, setOrders] = useState<BookOrder[]>([]);
  const [products, setProducts] = useState<BookProduct[]>([]);
  const [reviews, setReviews] = useState<BookReview[]>([]);
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [editing, setEditing] = useState<BookProduct | null>(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const authConfig = useCallback(
    () => ({
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      withCredentials: true,
    }),
    [accessToken],
  );

  const loadAll = useCallback(async () => {
    if (tab === 'palja') return;
    setLoading(true);
    setMessage('');
    try {
      const orderFilter = tab === 'unsent' ? '&unsent=1' : tab === 'sent' ? '&sent=1' : '';
      const [statsRes, ordersRes, productsRes, reviewsRes, emailsRes] = await Promise.all([
        axios.get('/api/admin/books/stats', authConfig()),
        axios.get(`/api/admin/orders?limit=100${orderFilter}`, authConfig()),
        axios.get('/api/admin/books/products', authConfig()),
        axios.get('/api/admin/books/reviews?pageSize=50', authConfig()),
        axios.get('/api/admin/email-logs?limit=100', authConfig()),
      ]);
      setStats(statsRes.data.stats);
      setOrders(ordersRes.data.orders ?? []);
      setProducts(productsRes.data.products ?? []);
      setReviews(reviewsRes.data.reviews ?? []);
      setEmailLogs(emailsRes.data.emailLogs ?? []);
    } catch (error) {
      setMessage(`불러오기 실패: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  }, [authConfig, tab]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const act = useCallback(
    async (run: () => Promise<unknown>, okMessage: string) => {
      setMessage('');
      try {
        await run();
        setMessage(okMessage);
        await loadAll();
      } catch (error) {
        const detail = axios.isAxiosError(error)
          ? error.response?.data?.error || error.message
          : String(error);
        setMessage(`실패: ${detail}`);
      }
    },
    [loadAll],
  );

  const shipOrder = (orderId: string) =>
    act(
      () => axios.post(`/api/admin/orders/${orderId}/ship`, {}, authConfig()),
      `${orderId} 출고 처리 완료`,
    );

  const cancelOrder = (orderId: string) => {
    if (!window.confirm(`${orderId} 주문을 취소할까요? (마일리지 회수/재고 복원)`)) return;
    return act(
      () => axios.post(`/api/admin/orders/${orderId}/cancel`, {}, authConfig()),
      `${orderId} 취소 완료`,
    );
  };

  const sendMail = (orderId: string) =>
    act(
      () => axios.post(`/api/reports/${orderId}/send-mail`, {}, authConfig()),
      `${orderId} 리포트 메일 발송 완료`,
    );

  const uploadOrderFile = (orderId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return act(
      () => axios.post(`/api/admin/orders/${orderId}/upload-file`, form, authConfig()),
      `${orderId} 자료 업로드 완료: ${file.name}`,
    );
  };

  const sendToApp = (orderId: string) =>
    act(
      () => axios.post(`/api/admin/orders/${orderId}/send-to-app`, {}, authConfig()),
      `${orderId} 발송 완료 — 앱 보관함에서 다운로드 가능합니다`,
    );

  const retryEmail = (logId: number) =>
    act(
      () => axios.post(`/api/admin/email-logs/${logId}/retry`, {}, authConfig()),
      `발송 로그 #${logId} 재발송 완료`,
    );

  const saveProduct = () => {
    if (!editing) return;
    return act(
      () => axios.post('/api/admin/books/products', editing, authConfig()),
      `상품 저장 완료: ${editing.productId}`,
    ).then(() => setEditing(null));
  };

  const deactivateProduct = (productId: string) =>
    act(
      () => axios.delete(`/api/admin/books/products/${productId}`, authConfig()),
      `상품 비노출 처리: ${productId}`,
    );

  const setReviewStatus = (reviewId: number, status: 'VISIBLE' | 'HIDDEN') =>
    act(
      () => axios.post(`/api/admin/books/reviews/${reviewId}/status`, { status }, authConfig()),
      `리뷰 #${reviewId} → ${status}`,
    );

  const hasPdfItem = (order: BookOrder) => order.items.some((i) => i.kind === 'pdf');

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">사주 책방 관리</h1>
        {tab !== 'palja' && <Button onClick={() => void loadAll()} disabled={loading}>
          {loading ? '로딩…' : '새로고침'}
        </Button>}
      </div>

      {tab !== 'palja' && stats && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
          {[
            ['전체 주문', String(stats.ordersTotal)],
            ['미발송', String(stats.ordersUnsent)],
            ['발송완료', String(stats.ordersSent)],
            ['출고완료', String(stats.ordersShipped)],
            ['매출', krw(stats.revenue)],
            ['발송실패', String(stats.emailFailed)],
          ].map(([label, value]) => (
            <Card key={label}>
              <CardHeader className="pb-1">
                <CardTitle className="text-xs text-gray-500">{label}</CardTitle>
              </CardHeader>
              <CardContent className="text-lg font-bold">{value}</CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-b" role="tablist" aria-label="책방 관리">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`px-3 py-2 text-sm font-bold ${
              tab === t.key ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'
            }`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab !== 'palja' && message && <p className="rounded border border-blue-200 bg-blue-50 p-2 text-sm">{message}</p>}

      {tab === 'palja' && <PaljaBookCatalogPanel token={accessToken} />}

      {(tab === 'orders' || tab === 'unsent' || tab === 'sent') && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>주문번호</TableHead>
              <TableHead>상품</TableHead>
              <TableHead>금액</TableHead>
              <TableHead>주문자</TableHead>
              <TableHead>수신인/이메일</TableHead>
              <TableHead>사주대상</TableHead>
              <TableHead>생년월일/시간(24시)</TableHead>
              <TableHead>양·음력/서머타임</TableHead>
              <TableHead>상태</TableHead>
              {tab === 'unsent' && <TableHead>자료</TableHead>}
              {tab === 'sent' && <TableHead>수신여부</TableHead>}
              <TableHead>처리</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((order) => (
              <TableRow key={order.orderId}>
                <TableCell className="font-mono text-xs">{order.orderId}</TableCell>
                <TableCell className="text-xs">
                  {order.items.map((i) => `${i.title}×${i.quantity}`).join(', ')}
                </TableCell>
                <TableCell>
                  {(() => {
                    const d = orderDiscount(order);
                    return d ? (
                      <>
                        <span className="mr-1 text-[11px] text-gray-400 line-through">{krw(d.listTotal)}</span>
                        <span className="text-[11px] font-semibold text-red-600">{d.rate}%↓</span>
                        <br />
                      </>
                    ) : null;
                  })()}
                  {krw(order.amountDue ?? order.amount)}
                  {(order.mileageUsed ?? 0) > 0 && (
                    <div className="text-[11px] text-gray-400">마일리지 -{krw(order.mileageUsed!)}</div>
                  )}
                </TableCell>
                <TableCell className="text-xs">{order.ordererName}</TableCell>
                <TableCell className="text-xs">
                  {order.recipientName || order.ordererName}
                  <br />
                  {order.email}
                </TableCell>
                <TableCell className="text-xs">{order.targetName}</TableCell>
                <TableCell className="text-xs">
                  {order.birthDate}
                  <br />
                  {order.birthTime || '시간 미입력'}
                </TableCell>
                <TableCell className="text-xs">
                  {order.calendarType === 'LUNAR' ? '음력' : '양력'}
                  <br />
                  <span className={order.dst ? 'text-blue-600' : 'text-gray-400'}>
                    서머타임 {order.dst ? '적용' : '없음'}
                  </span>
                </TableCell>
                <TableCell>
                  <Badge>{order.deliveryStatus || order.status}</Badge>
                  {order.deliveryStatus === '배송전' && order.deliveryDueAt && (
                    <div className="mt-1 text-[11px] text-amber-600">
                      기한 {order.deliveryDueAt.slice(0, 10)}
                    </div>
                  )}
                  {tab === 'sent' && order.sentAt && (
                    <div className="mt-1 text-[11px] text-gray-400">
                      발송 {order.sentAt.slice(0, 10)}
                    </div>
                  )}
                </TableCell>
                {tab === 'unsent' && (
                  <TableCell className="text-xs">
                    {order.pdfAvailable ? (
                      <span className="text-green-600">{order.pdfFileName ?? '업로드됨'}</span>
                    ) : (
                      <span className="text-gray-400">없음</span>
                    )}
                    <label className="mt-1 block w-fit cursor-pointer rounded-md border px-2 py-1 text-xs hover:bg-gray-50">
                      자료 업로드
                      <input
                        type="file"
                        accept="application/pdf"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) void uploadOrderFile(order.orderId, file);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </TableCell>
                )}
                {tab === 'sent' && (
                  <TableCell className="text-xs">
                    {!hasPdfItem(order) ? (
                      <span className="text-gray-400">— (실물)</span>
                    ) : (order.downloadCount ?? 0) > 0 ? (
                      <span className="font-bold text-green-600">
                        수신 ({order.downloadCount}회 다운로드)
                      </span>
                    ) : (
                      <span className="text-amber-600">미수신</span>
                    )}
                  </TableCell>
                )}
                <TableCell className="space-x-1 whitespace-nowrap">
                  {tab === 'unsent' && (
                    <Button
                      size="sm"
                      disabled={!order.pdfAvailable}
                      onClick={() => void sendToApp(order.orderId)}
                    >
                      발송
                    </Button>
                  )}
                  {order.deliveryStatus === '배송전' && (
                    <Button size="sm" onClick={() => void shipOrder(order.orderId)}>
                      출고 처리
                    </Button>
                  )}
                  {['PAID', 'GENERATING', 'GENERATED'].includes(order.status) && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => void sendMail(order.orderId)}>
                        메일 발송
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => void cancelOrder(order.orderId)}>
                        취소
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {tab === 'products' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setEditing({ ...EMPTY_PRODUCT })}>+ 책 업로드</Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>상품ID</TableHead>
                <TableHead>상품명</TableHead>
                <TableHead>정가/판매가</TableHead>
                <TableHead>판매/리뷰</TableHead>
                <TableHead>노출</TableHead>
                <TableHead>처리</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p.productId}>
                  <TableCell className="font-mono text-xs">{p.productId}</TableCell>
                  <TableCell>
                    {p.emoji} {p.title}{' '}
                    <Badge>{p.kind === 'pdf' ? 'PDF' : '실물'}</Badge>
                    {p.author ? (
                      <div className="text-[11px] text-gray-500">저자 {p.author}</div>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-xs">
                    <span className="text-gray-400 line-through">{krw(p.listPrice)}</span>{' '}
                    {krw(p.price)}
                  </TableCell>
                  <TableCell className="text-xs">
                    {p.salesCount}권 / ★{p.avgRating} ({p.reviewCount})
                    <div className="text-[11px] text-gray-400">
                      재고 {p.stock === null ? '무제한' : p.stock}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge>{p.active ? '노출' : '숨김'}</Badge>
                  </TableCell>
                  <TableCell className="space-x-1">
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...p })}>
                      수정
                    </Button>
                    {p.active && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void deactivateProduct(p.productId)}
                      >
                        숨김
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {editing && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {editing.productId ? `상품 수정: ${editing.productId}` : '새 책 업로드'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                  <Input
                    placeholder="상품ID (예: saju_book2)"
                    value={editing.productId}
                    onChange={(e) => setEditing({ ...editing, productId: e.target.value })}
                  />
                  <Input
                    placeholder="상품명"
                    value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  />
                  <Input
                    placeholder="정가"
                    type="number"
                    value={editing.listPrice || ''}
                    onChange={(e) => setEditing({ ...editing, listPrice: Number(e.target.value) })}
                  />
                  <Input
                    placeholder="판매가"
                    type="number"
                    value={editing.price || ''}
                    onChange={(e) => setEditing({ ...editing, price: Number(e.target.value) })}
                  />
                  <select
                    className="h-10 rounded-md border px-2 text-sm"
                    value={editing.kind}
                    onChange={(e) =>
                      setEditing({ ...editing, kind: e.target.value as 'pdf' | 'physical' })
                    }
                  >
                    <option value="pdf">PDF (이메일 발송)</option>
                    <option value="physical">실물 (택배 배송)</option>
                  </select>
                  <Input
                    placeholder="이모지"
                    value={editing.emoji}
                    onChange={(e) => setEditing({ ...editing, emoji: e.target.value })}
                  />
                  <Input
                    placeholder="저자"
                    value={editing.author || ''}
                    onChange={(e) => setEditing({ ...editing, author: e.target.value })}
                  />
                  <Input
                    placeholder="표지 URL (/static/book-covers/...)"
                    value={editing.coverUrl || ''}
                    onChange={(e) => setEditing({ ...editing, coverUrl: e.target.value })}
                  />
                  <Input
                    placeholder="정렬 순서"
                    type="number"
                    value={editing.sortOrder || ''}
                    onChange={(e) => setEditing({ ...editing, sortOrder: Number(e.target.value) })}
                  />
                  <Input
                    placeholder="재고 (빈칸=무제한)"
                    type="number"
                    value={editing.stock ?? ''}
                    onChange={(e) =>
                      setEditing({ ...editing, stock: e.target.value === '' ? null : Number(e.target.value) })
                    }
                  />
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={editing.active}
                      onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                    />
                    노출
                  </label>
                </div>
                <Input
                  placeholder="한 줄 설명"
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                />
                <textarea
                  className="min-h-28 w-full rounded-md border p-2 text-sm"
                  placeholder="상세페이지 HTML (어드민 업로드)"
                  value={editing.detailHtml}
                  onChange={(e) => setEditing({ ...editing, detailHtml: e.target.value })}
                />
                <div className="flex gap-2">
                  <Button onClick={() => void saveProduct()}>저장</Button>
                  <Button variant="outline" onClick={() => setEditing(null)}>
                    취소
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {tab === 'reviews' && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>상품</TableHead>
              <TableHead>작성자</TableHead>
              <TableHead>별점</TableHead>
              <TableHead>내용</TableHead>
              <TableHead>상태</TableHead>
              <TableHead>처리</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reviews.map((review) => (
              <TableRow key={review.id}>
                <TableCell>{review.id}</TableCell>
                <TableCell className="font-mono text-xs">{review.product_id}</TableCell>
                <TableCell className="text-xs">
                  {review.author_name}
                  <br />
                  {review.email}
                </TableCell>
                <TableCell>{'★'.repeat(review.rating)}</TableCell>
                <TableCell className="max-w-60 text-xs">{review.content}</TableCell>
                <TableCell>
                  <Badge>{review.status}</Badge>
                </TableCell>
                <TableCell>
                  {review.status === 'VISIBLE' ? (
                    <Button size="sm" variant="outline" onClick={() => void setReviewStatus(review.id, 'HIDDEN')}>
                      숨김
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => void setReviewStatus(review.id, 'VISIBLE')}>
                      노출
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {tab === 'emails' && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>주문번호</TableHead>
              <TableHead>수신</TableHead>
              <TableHead>상태</TableHead>
              <TableHead>시도</TableHead>
              <TableHead>오류</TableHead>
              <TableHead>처리</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {emailLogs.map((log) => (
              <TableRow key={log.id}>
                <TableCell>{log.id}</TableCell>
                <TableCell className="font-mono text-xs">{log.order_id}</TableCell>
                <TableCell className="text-xs">{log.to_email}</TableCell>
                <TableCell>
                  <Badge>{log.status}</Badge>
                </TableCell>
                <TableCell>{log.attempt}</TableCell>
                <TableCell className="max-w-60 text-xs text-red-600">{log.error}</TableCell>
                <TableCell>
                  {log.status === 'FAILED' && (
                    <Button size="sm" onClick={() => void retryEmail(log.id)}>
                      재발송
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
